// src/app/(dashboard)/meter/utils/pageFit.ts
//
// Pure sizing math for the meter list's "fit the screen without scrolling"
// behavior (see MeterListClient's recalcPageSize). Kept separate from the
// component so it can be unit tested without a DOM.

// Must match the server-side cap in src/lib/db/queries/meters.ts's
// listMeters (Math.min(100, ...)). Duplicated rather than imported because
// meters.ts is a server-only module that pulls in the live API client.
export const MAX_METER_PAGE_SIZE = 100;

const DEFAULT_GAP = 12; // matches the grid's gap-3 Tailwind class
const DEFAULT_BOTTOM_PADDING = 8;
const MIN_VIEWPORT_DIMENSION = 200;

export function getGridColumnCount(viewportWidth: number): number {
  if (viewportWidth >= 1280) return 4;
  if (viewportWidth >= 1024) return 3;
  if (viewportWidth >= 640) return 2;
  return 1;
}

export interface IdealPageSizeInput {
  viewportWidth: number;
  viewportHeight: number;
  gridTop: number;
  rowHeight: number;
  gap?: number;
  bottomPadding?: number;
}

// Returns how many cards should be requested to exactly fill the viewport
// with whole rows, or null when the inputs are too implausible to size for
// (e.g. a transient near-zero viewport during navigation/resize).
export function computeIdealPageSize({
  viewportWidth,
  viewportHeight,
  gridTop,
  rowHeight,
  gap = DEFAULT_GAP,
  bottomPadding = DEFAULT_BOTTOM_PADDING,
}: IdealPageSizeInput): number | null {
  if (
    viewportWidth < MIN_VIEWPORT_DIMENSION ||
    viewportHeight < MIN_VIEWPORT_DIMENSION
  ) {
    return null;
  }
  if (!Number.isFinite(rowHeight) || rowHeight <= 0) return null;

  const cols = getGridColumnCount(viewportWidth);
  const available = viewportHeight - gridTop - bottomPadding;
  const fitRows = Math.max(1, Math.floor((available + gap) / (rowHeight + gap)));
  const idealPageSize = fitRows * cols;

  if (idealPageSize <= 0) return null;
  return Math.min(MAX_METER_PAGE_SIZE, idealPageSize);
}
